/* ── Officer profiles & access config ────────────────────────────────────
 *
 * ── WHY THERE IS NO PASSWORD IN THIS FILE ──
 *
 * There used to be a plaintext `AUTH_PASSWORD` constant here. Create React
 * App inlines
 * every module into the served bundle, so that string was downloaded by
 * every visitor to the deployed demo and readable with View Source. The
 * platform spends §17 insisting that secrets do not belong client-side
 * while shipping one, which is the kind of contradiction that costs a
 * reviewer's trust in everything else.
 *
 * What is stored now is a salted SHA-256 digest, checked with Web Crypto.
 *
 * ── AND WHY THAT IS STILL NOT SECURITY ──
 *
 * Be clear about what this buys. A digest stops the password being READ
 * out of the bundle. It does not stop it being GUESSED: the salt and the
 * digest are both right here, so anyone can run a wordlist against them
 * offline, and a short human-chosen password will not survive that for
 * long. Do not reuse the demo code anywhere that matters.
 *
 * Client-side authentication cannot be made secure, because the client
 * decides the answer. This is a demo gate that keeps a casual visitor out
 * of a demonstration, and the honest description of it is obfuscation, not
 * access control. Real deployment needs the check to happen on a server
 * that the browser cannot argue with — which is exactly what §17 lists as
 * an architectural control this demonstration does not have.
 */

const AUTH_SALT = "inqai-kumbh-2027-demo";

/* Salted SHA-256 of the shared demo password. */
export const AUTH_PASSWORD_DIGEST =
  "21ef341f5d27d0692a8d2fe64cb5722c67714c121f8ee9a18c7c39bf3c595952";

/* Verify a candidate password. Async because Web Crypto is.
 *
 * Fails CLOSED when SubtleCrypto is unavailable rather than falling back to
 * a plaintext comparison — a fallback would reintroduce the exact problem
 * this replaces, and would do it silently. SubtleCrypto is present in every
 * secure context, which covers both localhost and the deployed HTTPS site,
 * so the closed path should never be reached in normal use. */
export async function verifyPassword(candidate) {
  const subtle = typeof window !== "undefined" && window.crypto?.subtle;
  /* TextEncoder ships with every browser that has SubtleCrypto, so this
     guard should never fire in practice. It exists because the caller
     awaits this inside a click handler: an unhandled throw there leaves
     the sign-in button spinning with no error and no way forward, which
     is a worse failure than a clear refusal. */
  if (!subtle || typeof TextEncoder === "undefined") {
    return {
      ok: false,
      reason:
        "This browser did not expose Web Crypto, so the access code cannot be checked. "
        + "Open the site over HTTPS or on localhost.",
    };
  }
  const bytes = new TextEncoder().encode(AUTH_SALT + String(candidate ?? ""));
  const digest = await subtle.digest("SHA-256", bytes);
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return { ok: hex === AUTH_PASSWORD_DIGEST, reason: null };
}

// Roster shown in the login "Officer Profile" dropdown.
//
// Profiles carry a POSITION and no personal name. The names that used to be
// here were invented, and an invented name attached to a real public office
// is the kind of detail that gets screenshotted and repeated as fact — a
// reader has no way to tell it apart from a real appointment. The post is
// what the demo needs to convey anyway; who holds it is not.
//
// `accent` is a 2-stop gradient used for the avatar chip (domain-coded).
export const OFFICERS = [
  {
    /* The Divisional Commissioner, Nashik chairs Simhastha Kumbh planning
     * for the division and sits above the District Collector in the
     * Maharashtra revenue hierarchy. The roster was missing the one post
     * that actually convenes the departments this platform coordinates.
     *
     * `clearance` maps the profile onto the §17 access model, so a role is
     * not merely a label on the login screen: it decides what the copilot
     * will answer and which fields survive redaction. */
    id: "div-commissioner",
    position: "Divisional Commissioner, Nashik Division",
    email: "divcom@nashikdivision.gov.in",
    level: "L1",
    access: "Full Access",
    clearance: "COMMAND",
    accent: ["#7E22CE", "#581C87"],
  },
  {
    /* The dedicated Kumbh Mela post. A Simhastha runs across district and
     * municipal boundaries and across a dozen departments, so the standing
     * revenue hierarchy alone cannot run it — Maharashtra appoints a
     * Kumbh-specific commissioner to hold the mela as a single operation.
     *
     * That is exactly the seat this platform is built for: the twin, the
     * mobility chain and the multi-agency workflow all model the theatre
     * whole rather than by department, and this is the only role whose
     * remit matches that scope. */
    id: "kumbh-commissioner",
    position: "Commissioner, Simhastha Kumbh Mela Authority",
    email: "commissioner@simhasthakumbh.gov.in",
    level: "L1",
    access: "Full Access",
    clearance: "COMMAND",
    accent: ["#B45309", "#78350F"],
  },
  {
    id: "collector",
    position: "District Collector & Magistrate, Nashik",
    email: "collector@nashik.gov.in",
    level: "L1",
    access: "Full Access",
    clearance: "COMMAND",
    accent: ["#1D4ED8", "#1E3A8A"],
  },
  {
    id: "cp",
    position: "Commissioner of Police, Nashik",
    email: "cp@nashikpolice.gov.in",
    level: "L1",
    access: "Full Access",
    clearance: "COMMAND",
    accent: ["#4338CA", "#3730A3"],
  },
  {
    id: "dcp",
    position: "Deputy Commissioner of Police, Nashik City",
    email: "dcp.city@nashikpolice.gov.in",
    level: "L2",
    access: "Operations",
    clearance: "DEPARTMENT",
    accent: ["#2563EB", "#1D4ED8"],
  },
  {
    id: "municipal",
    position: "Municipal Commissioner, Nashik",
    email: "commissioner@nmc.gov.in",
    level: "L2",
    access: "Civic Operations",
    clearance: "DEPARTMENT",
    accent: ["#0891B2", "#0E7490"],
  },
  {
    id: "cmo",
    position: "Chief Medical Officer, Nashik",
    email: "cmo@nashik.gov.in",
    level: "L2",
    access: "Health & Epidemic",
    clearance: "DEPARTMENT",
    accent: ["#059669", "#047857"],
  },
  {
    id: "sp-rural",
    position: "Superintendent of Police, Nashik Rural",
    email: "sp.rural@nashikpolice.gov.in",
    level: "L2",
    access: "Field Command",
    clearance: "DEPARTMENT",
    accent: ["#7C3AED", "#5B21B6"],
  },
  {
    id: "ndrf",
    position: "NDRF Commandant, Simhastha Deployment",
    email: "commandant@ndrf.gov.in",
    level: "L2",
    access: "Disaster Response",
    clearance: "DEPARTMENT",
    accent: ["#DC2626", "#B91C1C"],
  },
  {
    id: "nodal",
    position: "Maha Kumbh AI Nodal Officer, War Room",
    email: "nodal@mahakumbhai.gov.in",
    level: "L2",
    access: "Coordination",
    clearance: "COMMAND",
    accent: ["#0D9488", "#0F766E"],
  },
  {
    id: "mela",
    position: "Mela Adhikari, Trimbakeshwar",
    email: "mela.trimbak@nashik.gov.in",
    level: "L3",
    access: "Zone Operations",
    clearance: "FIELD",
    accent: ["#D97706", "#B45309"],
  },
  {
    /* Kumbhathon integration partner. Not a government officer — a
     * third-party developer from the startup ecosystem, given the lowest
     * clearance in the model.
     *
     * Included deliberately rather than as a convenience. An integration
     * partner is the role most likely to be over-granted in a real
     * deployment ("they need the data to build the feature"), and the
     * clearest demonstration that §17's controls are enforced rather than
     * decorative: signed in as this profile, the copilot refuses medical
     * capacity, unit positions are stripped from every payload, and the
     * platform behaves exactly as it does for a member of the public. */
    id: "kumbhathon",
    position: "Integration Partner — Kumbhathon Ecosystem",
    email: "partner@kumbhathon.example",
    level: "L4",
    access: "Public Data Only",
    clearance: "PUBLIC",
    external: true,
    accent: ["#0F766E", "#134E4A"],
  },
];

// Two-letter badge from a position, skipping filler words —
// e.g. "Commissioner of Police, Nashik" → "CP", "Chief Medical Officer" → "CM".
const POSITION_STOPWORDS = new Set(["of", "and", "the", "for", "at", "in", "on", "to"]);
export function positionInitials(position = "") {
  const words = position
    .replace(/[,&]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => !POSITION_STOPWORDS.has(w.toLowerCase()));
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export function findOfficer(id) {
  return OFFICERS.find((o) => o.id === id) || null;
}
